import { useCallback, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { pastExportsOptions } from '@/features/DataHub/api';
import {
  createUniqueExportName,
  createUniqueExportNameFromBaseName
} from '@/features/DataHub/modules/Export/services/ExportNameService';
import { useAppSelector } from '@/hooks/redux-hooks';
import useDateText, { DateTextVariants } from '@/hooks/useDateText';

const useExportName = () => {
  const [exportNameOverride, setExportNameOverride] = useState<string | null | undefined>();
  const { formatDateString } = useDateText();
  const { data: pastExports = [] } = useQuery(pastExportsOptions.list());
  const exportNameBase = useAppSelector(state => state.export.exportNameBase);

  const createExportName = useCallback((): string => {
    const normalizedExportNameBase = exportNameBase?.trim();
    const existingExports = pastExports.map(e => ({ fileName: e.name }));

    if (normalizedExportNameBase) {
      return createUniqueExportNameFromBaseName(normalizedExportNameBase, existingExports);
    }

    return createUniqueExportName(existingExports, (isoDate: string) =>
      formatDateString(isoDate, DateTextVariants.FULL)
    );
  }, [exportNameBase, pastExports, formatDateString]);

  const exportName = exportNameOverride === undefined ? createExportName() : exportNameOverride;

  const setExportName = useCallback((name: string | null) => {
    setExportNameOverride(name);
  }, []);

  return { exportName, setExportName, createExportName };
};

export default useExportName;
